import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft, Calculator, TrendingUp, Info, AlertTriangle } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area, PieChart, Pie, Cell } from 'recharts';

const STORAGE_KEY = 'sip_calculator_data';

export const SIPCalculator = () => {
  const [monthlySIP, setMonthlySIP] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).monthlySIP : 5000;
  });
  
  const [expectedReturn, setExpectedReturn] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).expectedReturn : 12;
  });
  
  const [investmentDuration, setInvestmentDuration] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).investmentDuration : 10;
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      monthlySIP,
      expectedReturn,
      investmentDuration
    }));
  }, [monthlySIP, expectedReturn, investmentDuration]);

  const calculateSIP = () => {
    const monthly = parseFloat(monthlySIP) || 0;
    const annualRate = (parseFloat(expectedReturn) || 0) / 100;
    const years = parseFloat(investmentDuration) || 0;
    const monthlyRate = annualRate / 12;
    const months = years * 12;

    const totalInvested = monthly * months;
    let finalCorpus = 0;

    if (monthlyRate > 0 && months > 0) {
      finalCorpus = monthly * ((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate) * (1 + monthlyRate);
    } else {
      finalCorpus = totalInvested;
    }

    const wealthCreated = finalCorpus - totalInvested;
    
    // XIRR approximation (using CAGR as approximation)
    const xirr = years > 0 ? (Math.pow(finalCorpus / totalInvested, 1 / years) - 1) * 100 : 0;

    return {
      totalInvested,
      finalCorpus,
      wealthCreated,
      xirr,
      months
    };
  };

  const generateMonthlyGrowth = () => {
    const monthly = parseFloat(monthlySIP) || 0;
    const annualRate = (parseFloat(expectedReturn) || 0) / 100;
    const years = parseFloat(investmentDuration) || 0;
    const monthlyRate = annualRate / 12;
    const totalMonths = years * 12;
    
    const data = [];
    const step = Math.max(1, Math.floor(totalMonths / 20)); // Show ~20 data points
    
    for (let month = 0; month <= totalMonths; month += step) {
      const invested = monthly * month;
      let corpus = 0;
      
      if (monthlyRate > 0 && month > 0) {
        corpus = monthly * ((Math.pow(1 + monthlyRate, month) - 1) / monthlyRate) * (1 + monthlyRate);
      } else {
        corpus = invested;
      }
      
      data.push({
        month: month,
        label: `M${month}`,
        invested: Math.round(invested),
        corpus: Math.round(corpus),
        returns: Math.round(corpus - invested)
      });
    }
    
    return data;
  };

  const generateYearlyGrowth = () => {
    const monthly = parseFloat(monthlySIP) || 0;
    const annualRate = (parseFloat(expectedReturn) || 0) / 100;
    const years = parseFloat(investmentDuration) || 0;
    const monthlyRate = annualRate / 12;
    
    const data = [];
    
    for (let year = 0; year <= years; year++) {
      const months = year * 12;
      const invested = monthly * months;
      let corpus = 0;
      
      if (monthlyRate > 0 && months > 0) {
        corpus = monthly * ((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate) * (1 + monthlyRate);
      } else {
        corpus = invested;
      }
      
      data.push({
        year: `Year ${year}`,
        invested: Math.round(invested),
        corpus: Math.round(corpus)
      });
    }
    
    return data;
  };

  const generateComparisonData = () => {
    const monthly = parseFloat(monthlySIP) || 0;
    const annualRate = (parseFloat(expectedReturn) || 0) / 100;
    const years = parseFloat(investmentDuration) || 0;
    const monthlyRate = annualRate / 12;
    const months = years * 12;
    
    // SIP calculation
    const sipInvested = monthly * months;
    const sipCorpus = monthly * ((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate) * (1 + monthlyRate);
    
    // Lump sum calculation (investing total SIP amount upfront)
    const lumpsum = sipInvested;
    const lumpsumCorpus = lumpsum * Math.pow(1 + annualRate, years);
    
    return [
      { type: 'SIP', corpus: Math.round(sipCorpus) },
      { type: 'Lump Sum', corpus: Math.round(lumpsumCorpus) }
    ];
  };

  const generateStepUpScenarios = () => {
    const monthly = parseFloat(monthlySIP) || 0;
    const annualRate = (parseFloat(expectedReturn) || 0) / 100;
    const years = parseFloat(investmentDuration) || 0;
    const monthlyRate = annualRate / 12;
    
    const stepUpRates = [0, 5, 10, 15];
    
    return stepUpRates.map(stepUp => {
      let totalInvested = 0;
      let corpus = 0;
      let currentSIP = monthly;
      
      for (let year = 0; year < years; year++) {
        for (let month = 0; month < 12; month++) {
          totalInvested += currentSIP;
          corpus = (corpus + currentSIP) * (1 + monthlyRate);
        }
        // Increase SIP at the end of each year
        currentSIP = currentSIP * (1 + stepUp / 100);
      }
      
      return {
        stepUp: `${stepUp}%`,
        corpus: Math.round(corpus),
        invested: Math.round(totalInvested)
      };
    });
  };

  const results = calculateSIP();
  const monthlyGrowth = generateMonthlyGrowth();
  const yearlyGrowth = generateYearlyGrowth();
  const comparisonData = generateComparisonData();
  const stepUpData = generateStepUpScenarios();

  const COLORS = ['hsl(var(--primary))', 'hsl(var(--secondary))', 'hsl(var(--accent))', 'hsl(var(--purple))'];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <div className="min-h-screen bg-gradient-hero">
      <nav className="border-b border-border/40 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              </Link>
              <Separator orientation="vertical" className="h-6" />
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg gradient-primary flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-white" />
                </div>
                <span className="font-display text-lg font-semibold text-foreground">
                  SIP Calculator
                </span>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="calculator" className="space-y-6">
          <div className="overflow-x-auto scrollbar-hide -mx-4 px-4">
            <TabsList className="inline-flex w-auto min-w-full sm:min-w-0 bg-muted/50 p-1 h-auto">
              <TabsTrigger 
                value="calculator" 
                className="data-[state=active]:bg-primary data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <Calculator className="w-4 h-4 mr-2" />
                Calculator & Results
              </TabsTrigger>
              <TabsTrigger 
                value="charts" 
                className="data-[state=active]:bg-primary data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                Visual Analysis
              </TabsTrigger>
              <TabsTrigger 
                value="insights" 
                className="data-[state=active]:bg-primary data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <Info className="w-4 h-4 mr-2" />
                Insights & Explanation
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="calculator" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">What is a Systematic Investment Plan (SIP)?</CardTitle>
                <CardDescription className="text-base leading-relaxed">
                  SIP is a disciplined investment approach where you invest a fixed amount regularly (monthly) in mutual funds. 
                  It helps you benefit from Dollar cost averaging and the power of compounding, making wealth creation systematic and stress-free.
                </CardDescription>
              </CardHeader>
            </Card>

            <div className="grid lg:grid-cols-3 gap-6">
              <Card className="lg:col-span-1 border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Input Parameters</CardTitle>
                  <CardDescription>Enter your SIP details</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="monthlySIP" className="text-sm font-medium">
                      Monthly SIP Amount ($)
                    </Label>
                    <Input
                      id="monthlySIP"
                      type="number"
                      value={monthlySIP}
                      onChange={(e) => setMonthlySIP(e.target.value)}
                      placeholder="5000"
                      className="text-base"
                    />
                    <p className="text-xs text-muted-foreground">Amount you'll invest every month</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="expectedReturn" className="text-sm font-medium">
                      Expected Annual Return (%)
                    </Label>
                    <Input
                      id="expectedReturn"
                      type="number"
                      value={expectedReturn}
                      onChange={(e) => setExpectedReturn(e.target.value)}
                      placeholder="12"
                      step="0.1"
                      className="text-base"
                    />
                    <p className="text-xs text-muted-foreground">Historical equity returns: 10-15%</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="investmentDuration" className="text-sm font-medium">
                      Investment Duration (Years)
                    </Label>
                    <Input
                      id="investmentDuration"
                      type="number"
                      value={investmentDuration}
                      onChange={(e) => setInvestmentDuration(e.target.value)}
                      placeholder="10"
                      className="text-base"
                    />
                    <p className="text-xs text-muted-foreground">Longer = better compounding</p>
                  </div>

                  <Button 
                    className="w-full gradient-primary hover:opacity-90 transition-opacity shadow-secondary"
                    onClick={() => setMonthlySIP(monthlySIP)}
                  >
                    <Calculator className="w-4 h-4 mr-2" />
                    Calculate
                  </Button>
                </CardContent>
              </Card>

              <div className="lg:col-span-2 space-y-6">
                <Card className="border-border/50 shadow-md">
                  <CardHeader>
                    <CardTitle className="font-display">SIP Investment Results</CardTitle>
                    <CardDescription>Your wealth creation through systematic investing</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div className="p-6 rounded-xl bg-gradient-to-br from-primary-light to-card border border-primary/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
                            <TrendingUp className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Total Invested Amount</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-primary">
                          {formatCurrency(results.totalInvested)}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">{results.months} monthly contributions</p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-secondary-light to-card border border-secondary/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center">
                            <Calculator className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Final Corpus</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-secondary">
                          {formatCurrency(results.finalCorpus)}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">after {investmentDuration} years</p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-accent-light to-card border border-accent/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-accent flex items-center justify-center">
                            <TrendingUp className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Total Wealth Created</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-accent">
                          {formatCurrency(results.wealthCreated)}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                          {((results.wealthCreated / results.totalInvested) * 100).toFixed(1)}% growth
                        </p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-purple-light to-card border border-purple/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-purple flex items-center justify-center">
                            <Calculator className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">XIRR (Approx)</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-purple">
                          {results.xirr.toFixed(2)}%
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">Annual return rate</p>
                      </div>
                    </div>

                    <Separator className="my-6" />

                    <div className="p-6 rounded-xl bg-gradient-chart">
                      <div className="grid sm:grid-cols-2 gap-6">
                        <div>
                          <p className="text-sm font-medium text-muted-foreground mb-2">Monthly Contribution</p>
                          <p className="text-2xl font-display font-bold text-foreground mb-1">
                            {formatCurrency(parseFloat(monthlySIP))}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            For {results.months} months
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-muted-foreground mb-2">Returns Multiplier</p>
                          <p className="text-2xl font-display font-bold text-foreground mb-1">
                            {(results.finalCorpus / results.totalInvested).toFixed(2)}x
                          </p>
                          <p className="text-sm text-muted-foreground">
                            Your money grows by this factor
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="charts" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">Monthly Investment vs Corpus Growth</CardTitle>
                <CardDescription className="text-base">
                  See how your regular investments compound over time
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={monthlyGrowth}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis 
                        dataKey="label" 
                        stroke="hsl(var(--muted-foreground))"
                      />
                      <YAxis 
                        stroke="hsl(var(--muted-foreground))"
                        tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                        formatter={(value) => formatCurrency(value)}
                      />
                      <Legend />
                      <Area 
                        type="monotone" 
                        dataKey="invested" 
                        stackId="1"
                        stroke="hsl(var(--muted-foreground))" 
                        fill="hsl(var(--muted))" 
                        name="Total Invested"
                      />
                      <Area 
                        type="monotone" 
                        dataKey="returns" 
                        stackId="1"
                        stroke="hsl(var(--secondary))" 
                        fill="hsl(var(--secondary))" 
                        name="Returns"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Power of Compounding in SIP</CardTitle>
                  <CardDescription>
                    Year-wise growth trajectory
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={yearlyGrowth}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis 
                          dataKey="year" 
                          stroke="hsl(var(--muted-foreground))"
                          angle={-15}
                          textAnchor="end"
                          height={60}
                        />
                        <YAxis 
                          stroke="hsl(var(--muted-foreground))"
                          tickFormatter={(value) => `$${(value / 100000).toFixed(0)}L`}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--card))', 
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                          formatter={(value) => formatCurrency(value)}
                        />
                        <Legend />
                        <Line 
                          type="monotone" 
                          dataKey="invested" 
                          stroke="hsl(var(--muted-foreground))" 
                          strokeWidth={2}
                          name="Invested"
                        />
                        <Line 
                          type="monotone" 
                          dataKey="corpus" 
                          stroke="hsl(var(--primary))" 
                          strokeWidth={3}
                          name="Corpus Value"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">SIP vs Lump Sum Comparison</CardTitle>
                  <CardDescription>
                    Investing same total amount differently
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={comparisonData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis 
                          dataKey="type" 
                          stroke="hsl(var(--muted-foreground))"
                        />
                        <YAxis 
                          stroke="hsl(var(--muted-foreground))"
                          tickFormatter={(value) => `$${(value / 100000).toFixed(0)}L`}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--card))', 
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                          formatter={(value) => formatCurrency(value)}
                        />
                        <Bar dataKey="corpus" name="Final Corpus">
                          {comparisonData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">Step-Up SIP Impact</CardTitle>
                <CardDescription className="text-base">
                  How increasing your SIP annually boosts wealth creation
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[350px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={stepUpData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis 
                        dataKey="stepUp" 
                        stroke="hsl(var(--muted-foreground))"
                        label={{ value: 'Annual Step-Up Rate', position: 'insideBottom', offset: -5 }}
                      />
                      <YAxis 
                        stroke="hsl(var(--muted-foreground))"
                        tickFormatter={(value) => `$${(value / 100000).toFixed(0)}L`}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                        formatter={(value) => formatCurrency(value)}
                      />
                      <Legend />
                      <Bar dataKey="invested" fill="hsl(var(--muted))" name="Total Invested" />
                      <Bar dataKey="corpus" fill="hsl(var(--accent))" name="Final Corpus" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="insights" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">Understanding SIP Investing</CardTitle>
                <CardDescription className="text-base">
                  Master the art of systematic wealth creation
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-primary">
                    Dollar Cost Averaging
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed mb-3">
                    With your monthly SIP of <strong className="text-primary">{formatCurrency(parseFloat(monthlySIP))}</strong>, you buy more units when 
                    markets are down and fewer when they're up. This averaging effect reduces the impact of market volatility on your investments.
                  </p>
                  <div className="p-4 rounded-lg bg-primary-light border border-primary/20">
                    <p className="text-sm font-semibold mb-2">Example of Dollar Cost Averaging:</p>
                    <div className="space-y-1 text-sm">
                      <p>• Month 1: Market high - NAV $100 - Buy 50 units with $5,000</p>
                      <p>• Month 2: Market correction - NAV $80 - Buy 62.5 units with $5,000</p>
                      <p>• Month 3: Market recovery - NAV $110 - Buy 45.5 units with $5,000</p>
                      <p className="font-semibold mt-2">Average cost per unit: $94.94 (better than buying all at $100!)</p>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-secondary">
                    Long-Term Wealth Creation Logic
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed mb-3">
                    Your total investment of <strong className="text-secondary">{formatCurrency(results.totalInvested)}</strong> over {investmentDuration} years 
                    can grow to <strong className="text-secondary">{formatCurrency(results.finalCorpus)}</strong>, creating wealth of 
                    <strong className="text-secondary"> {formatCurrency(results.wealthCreated)}</strong>.
                  </p>
                  <div className="grid sm:grid-cols-3 gap-4">
                    <div className="p-4 rounded-lg bg-muted/50">
                      <p className="text-sm font-medium text-muted-foreground mb-1">Short-term (5 years)</p>
                      <p className="text-lg font-bold text-foreground">
                        {formatCurrency(parseFloat(monthlySIP) * ((Math.pow(1 + (parseFloat(expectedReturn)/100)/12, 60) - 1) / ((parseFloat(expectedReturn)/100)/12)) * (1 + (parseFloat(expectedReturn)/100)/12))}
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-secondary-light">
                      <p className="text-sm font-medium text-muted-foreground mb-1">Medium-term (10 years)</p>
                      <p className="text-lg font-bold text-secondary">
                        {formatCurrency(parseFloat(monthlySIP) * ((Math.pow(1 + (parseFloat(expectedReturn)/100)/12, 120) - 1) / ((parseFloat(expectedReturn)/100)/12)) * (1 + (parseFloat(expectedReturn)/100)/12))}
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-accent-light">
                      <p className="text-sm font-medium text-muted-foreground mb-1">Long-term (20 years)</p>
                      <p className="text-lg font-bold text-accent">
                        {formatCurrency(parseFloat(monthlySIP) * ((Math.pow(1 + (parseFloat(expectedReturn)/100)/12, 240) - 1) / ((parseFloat(expectedReturn)/100)/12)) * (1 + (parseFloat(expectedReturn)/100)/12))}
                      </p>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-destructive">
                    Risk: Stopping SIP Early
                  </h3>
                  <div className="space-y-3 text-base text-muted-foreground leading-relaxed">
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Breaking the Discipline:</strong> Stopping your SIP during market 
                        downturns is the worst time. You miss buying units at lower prices, which typically gives the best long-term returns.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Lost Compounding:</strong> Each month you pause, you lose the 
                        compounding effect on that month's investment for the entire remaining period.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Impact Example:</strong> If you stop your SIP for just 2 years in the 
                        middle of a 20-year period, you could lose 15-20% of your final corpus!
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 p-4 rounded-lg bg-destructive/10 border-l-4 border-destructive">
                    <p className="font-semibold text-destructive mb-1">🛑 Golden Rule</p>
                    <p className="text-sm">Continue your SIP especially during market downturns. Set it and forget it!</p>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-purple">
                    Suggestion: Step-Up SIP Advantage
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed mb-4">
                    As your income grows, increasing your SIP amount by 10-15% annually can significantly boost your wealth. 
                    This keeps pace with inflation and accelerates your goal achievement.
                  </p>
                  <div className="space-y-4 text-base">
                    <div className="p-4 rounded-lg bg-primary-light border-l-4 border-primary">
                      <h4 className="font-semibold text-foreground mb-2">✅ Scenario 1: Regular SIP</h4>
                      <p className="text-muted-foreground">
                        {formatCurrency(parseFloat(monthlySIP))} monthly for {investmentDuration} years = <strong>{formatCurrency(results.finalCorpus)}</strong>
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-secondary-light border-l-4 border-secondary">
                      <h4 className="font-semibold text-foreground mb-2">✅ Scenario 2: 10% Annual Step-Up</h4>
                      <p className="text-muted-foreground">
                        Starting at {formatCurrency(parseFloat(monthlySIP))}, increasing 10% yearly = 
                        <strong> {formatCurrency(stepUpData[2]?.corpus || 0)}</strong>
                        <span className="text-secondary font-semibold"> (+{(((stepUpData[2]?.corpus || 0) / results.finalCorpus - 1) * 100).toFixed(1)}%)</span>
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-accent-light border-l-4 border-accent">
                      <h4 className="font-semibold text-foreground mb-2">✅ Scenario 3: 15% Annual Step-Up</h4>
                      <p className="text-muted-foreground">
                        Starting at {formatCurrency(parseFloat(monthlySIP))}, increasing 15% yearly = 
                        <strong> {formatCurrency(stepUpData[3]?.corpus || 0)}</strong>
                        <span className="text-accent font-semibold"> (+{(((stepUpData[3]?.corpus || 0) / results.finalCorpus - 1) * 100).toFixed(1)}%)</span>
                      </p>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-accent">
                    When to Start and Important Tips
                  </h3>
                  <div className="space-y-3 text-base text-muted-foreground leading-relaxed">
                    <div className="flex gap-3">
                      <span className="text-accent font-semibold flex-shrink-0">✅</span>
                      <div>
                        <strong className="text-foreground">Start Early:</strong> The best time to start was yesterday, the 
                        second best time is today. Even starting 5 years earlier can double your final corpus!
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-accent font-semibold flex-shrink-0">✅</span>
                      <div>
                        <strong className="text-foreground">Automate It:</strong> Set up auto-debit so you never miss a payment. 
                        This removes emotional decision-making from the equation.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-accent font-semibold flex-shrink-0">✅</span>
                      <div>
                        <strong className="text-foreground">Align with Goals:</strong> Match your SIP tenure with your financial 
                        goals. Longer tenure for retirement, shorter for buying a house.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-accent font-semibold flex-shrink-0">✅</span>
                      <div>
                        <strong className="text-foreground">Review Annually:</strong> Check your fund performance once a year, 
                        but don't react to short-term market movements. Stay invested!
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default SIPCalculator;